<template>
  <div v-if="isOpen" class="pdf-overlay" @click.self="close">
    <div class="pdf-container">
      <div class="pdf-header">
        <h3>HVL - Page {{ currentPage }}</h3>
        <button @click="close" class="close-btn">&times;</button>
      </div>

      <div class="pdf-controls">
        <button @click="previousPage" :disabled="currentPage <= 1">Previous</button>
        <span>Page {{ currentPage }} / {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages">Next</button>
        <div class="zoom-controls">
          <button @click="zoomOut" :disabled="scale <= 0.5">-</button>
          <span>{{ Math.round(scale * 100) }}%</span>
          <button @click="zoomIn" :disabled="scale >= 3">+</button>
        </div>
      </div>

      <div class="pdf-canvas-container">
        <canvas ref="pdfCanvas"></canvas>
        <div v-if="loading" class="loading">Loading PDF...</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker - use CDN to avoid MIME type issues
// This matches the pdfjs-dist version in package.json (5.4.624)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.624/build/pdf.worker.min.mjs`;

// Physical page numbers match electronic page numbers (PDF has been fixed)
const PAGE_NUMBER_OFFSET = 0;

export default {
  name: 'PDFViewer',
  data() {
    return {
      isOpen: false,
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      loading: false,
      pageRendering: false,
      pageNumPending: null,
      scale: this.getSavedScale(),
      pdfPageOffset: 0, // Offset to map display page to PDF page within loaded chunk
      loadedPageRange: { start: 0, end: 0 }, // Track loaded page range
      loadedRanges: [], // Array of {start, end, pdfDoc, offset} for all loaded ranges
      allLoadedPageNumbers: new Set() // Set of all loaded page numbers for quick lookup
    };
  },
  methods: {
    getSavedScale() {
      // Get saved scale from local storage, default to 2 if not set
      const savedScale = localStorage.getItem('pdfViewerScale');
      if (savedScale) {
        const scale = parseFloat(savedScale);
        // Ensure scale is within valid range
        if (scale >= 0.5 && scale <= 3) {
          return scale;
        }
      }
      return 2; // Default scale
    },

    saveScale(scale) {
      // Save scale to local storage
      localStorage.setItem('pdfViewerScale', scale.toString());
    },

    async open(pageNumber) {
      this.isOpen = true;
      this.currentPage = pageNumber;
      this.loading = true;

      try {
        // First, get PDF info to know total pages
        if (!this.totalPages) {
          await this.fetchPdfInfo();
        }

        // Load pages from API (current page ± 3 pages)
        await this.loadPagesFromApi();

        this.loading = false;

        // Render the target page
        console.log(`Opening PDF at page ${pageNumber} (physical page ${this.currentPage})`);
        await this.renderPage(this.currentPage);
      } catch (error) {
        console.error('Error loading PDF:', error);
        this.loading = false;
        alert('Failed to load PDF. Please try again.');
      }
    },

    close() {
      this.isOpen = false;
      // Clean up all loaded PDF documents
      for (const range of this.loadedRanges) {
        if (range.pdfDoc) {
          range.pdfDoc.destroy();
        }
      }
      // Reset all tracking
      this.loadedRanges = [];
      this.allLoadedPageNumbers.clear();
      this.pdfDoc = null;
      console.log('Closed PDF viewer and freed all memory');
    },

    async fetchPdfInfo() {
      try {
        const response = await fetch(`${this.$server.apiUrl}/api/pdf/hvl/info`);
        if (!response.ok) {
          throw new Error('Failed to fetch PDF info');
        }
        const info = await response.json();
        // Total pages in the API response is for electronic pages
        // We need to subtract the offset to get the max physical page number
        this.totalPages = info.totalPages - PAGE_NUMBER_OFFSET;
        console.log('PDF Info:', info, 'Adjusted totalPages:', this.totalPages);
      } catch (error) {
        console.error('Error fetching PDF info:', error);
        throw error;
      }
    },

    async loadPagesFromApi() {
      try {
        // Calculate page range (current page ± 3 pages) in physical page numbers
        const startPage = Math.max(1, this.currentPage - 3);
        const endPage = Math.min(this.totalPages, this.currentPage + 3);

        // Check if current page is already loaded
        const existingRange = this.loadedRanges.find(
          range => this.currentPage >= range.start && this.currentPage <= range.end
        );

        if (existingRange) {
          console.log(`Page ${this.currentPage} already loaded in range ${existingRange.start}-${existingRange.end}`);
          this.pdfDoc = existingRange.pdfDoc;
          this.pdfPageOffset = existingRange.offset;
          this.loadedPageRange = { start: existingRange.start, end: existingRange.end };
          return; // Page already loaded, no need to fetch
        }

        // Find pages that need to be loaded
        const pagesToLoad = [];
        for (let p = startPage; p <= endPage; p++) {
          if (!this.allLoadedPageNumbers.has(p)) {
            pagesToLoad.push(p);
          }
        }

        if (pagesToLoad.length === 0) {
          console.log('All pages already loaded');
          return;
        }

        // Convert to electronic page numbers (add offset if needed)
        const startElectronicPage = startPage + PAGE_NUMBER_OFFSET;
        const endElectronicPage = endPage + PAGE_NUMBER_OFFSET;
        const pageRange = `${startElectronicPage}-${endElectronicPage}`;

        console.log('Loading NEW pages from API:', {
          currentPage: this.currentPage,
          physicalRange: `${startPage}-${endPage}`,
          electronicRange: `${startElectronicPage}-${endElectronicPage}`,
          newPages: pagesToLoad,
          totalLoadedBefore: this.allLoadedPageNumbers.size,
          apiUrl: `${this.$server.apiUrl}/api/pdf/hvl/pages?pages=${pageRange}`
        });

        // Fetch PDF with only the needed pages
        const response = await fetch(
          `${this.$server.apiUrl}/api/pdf/hvl/pages?pages=${pageRange}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch PDF pages');
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();

        // Load the PDF document
        const loadingTask = pdfjsLib.getDocument({
          data: arrayBuffer
        });

        const newPdfDoc = await loadingTask.promise;

        // Store this range
        const rangeInfo = {
          start: startPage,
          end: endPage,
          pdfDoc: newPdfDoc,
          offset: startPage - 1 // offset to convert page number to PDF page index
        };

        this.loadedRanges.push(rangeInfo);

        // Mark these pages as loaded
        for (let p = startPage; p <= endPage; p++) {
          this.allLoadedPageNumbers.add(p);
        }

        // Set as current PDF
        this.pdfDoc = newPdfDoc;
        this.pdfPageOffset = rangeInfo.offset;
        this.loadedPageRange = { start: startPage, end: endPage };

        console.log(`Loaded PDF with ${newPdfDoc.numPages} pages, offset = ${this.pdfPageOffset}`);
        console.log(`Total pages in memory: ${this.allLoadedPageNumbers.size}`);
      } catch (error) {
        console.error('Error loading pages from API:', error);
        throw error;
      }
    },

    async renderPage(pageNum) {
      if (this.pageRendering) {
        this.pageNumPending = pageNum;
        return;
      }

      this.scale = this.getSavedScale(); // Ensure we use the saved scale when rendering

      this.pageRendering = true;

      try {
        // Find which PDF document contains this page
        const rangeWithPage = this.loadedRanges.find(
          range => pageNum >= range.start && pageNum <= range.end
        );

        if (!rangeWithPage) {
          console.error(`Page ${pageNum} not loaded!`);
          this.pageRendering = false;
          return;
        }

        // Use the PDF document for this range
        const pdfToUse = rangeWithPage.pdfDoc;
        const offset = rangeWithPage.offset;

        // Convert display page number to PDF page number in the extracted document
        const pdfPageNum = pageNum - offset;

        console.log(`Rendering physical page ${pageNum} from range ${rangeWithPage.start}-${rangeWithPage.end}, PDF page ${pdfPageNum}`);

        const page = await pdfToUse.getPage(pdfPageNum);
        const viewport = page.getViewport({ scale: this.scale });

        const canvas = this.$refs.pdfCanvas;
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;

        this.pageRendering = false;

        // If there's a pending page, render it
        if (this.pageNumPending !== null) {
          const pending = this.pageNumPending;
          this.pageNumPending = null;
          await this.renderPage(pending);
        }
      } catch (error) {
        console.error('Error rendering page:', error);
        this.pageRendering = false;

        // Don't retry on error - just log it
        alert(`Failed to render page ${pageNum}. Please try refreshing.`);
      }
    },

    async previousPage() {
      if (this.currentPage <= 1) return;

      const newPage = this.currentPage - 1;

      console.log('Previous page:', {
        currentPage: this.currentPage,
        newPage,
        loadedRange: this.loadedPageRange,
        needsReload: newPage < this.loadedPageRange.start || newPage > this.loadedPageRange.end
      });

      // Check if we need to load more pages BEFORE changing currentPage
      if (newPage < this.loadedPageRange.start || newPage > this.loadedPageRange.end) {
        this.currentPage = newPage;
        this.loading = true;
        await this.loadPagesFromApi();
        this.loading = false;
      } else {
        this.currentPage = newPage;
      }

      await this.renderPage(this.currentPage);
    },

    async nextPage() {
      if (this.currentPage >= this.totalPages) return;

      const newPage = this.currentPage + 1;

      console.log('Next page:', {
        currentPage: this.currentPage,
        newPage,
        loadedRange: this.loadedPageRange,
        needsReload: newPage < this.loadedPageRange.start || newPage > this.loadedPageRange.end
      });

      // Check if we need to load more pages BEFORE changing currentPage
      if (newPage < this.loadedPageRange.start || newPage > this.loadedPageRange.end) {
        this.currentPage = newPage;
        this.loading = true;
        await this.loadPagesFromApi();
        this.loading = false;
      } else {
        this.currentPage = newPage;
      }

      await this.renderPage(this.currentPage);
    },

    async zoomIn() {
      if (this.scale >= 3) return;
      this.scale += 0.25;
      this.saveScale(this.scale);
      await this.renderPage(this.currentPage);
    },

    async zoomOut() {
      if (this.scale <= 0.5) return;
      this.scale -= 0.25;
      this.saveScale(this.scale);
      await this.renderPage(this.currentPage);
    }
  }
};
</script>

<style scoped>
.pdf-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.pdf-container {
  background: white;
  border-radius: 8px;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.pdf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.pdf-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #666;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
}

.close-btn:hover {
  color: #000;
}

.pdf-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 20px;
  padding-left: 20px;
  border-left: 1px solid #e0e0e0;
}

.pdf-controls button {
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.pdf-controls button:hover:not(:disabled) {
  background-color: #0056b3;
}

.pdf-controls button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pdf-canvas-container {
  flex: 1;
  overflow: auto;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
}

canvas {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  height: auto;
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 20px 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-size: 16px;
}
</style>
